/**
 * The minimap: a small top-down map in the top right corner.
 *
 * North up, the player at the middle as an arrow pointing the way they face.
 * The walls and the solid things near them, the room they are in picked out,
 * and where the objective line says to go as a gold marker — pinned to the
 * rim, pointing, when it is further than the map reaches — with the route to
 * it as a dashed gold line, the same one drawn on the floor. Errands and
 * things waiting to be fetched are smaller amber marks.
 *
 * People are drawn **only when the player can see or hear them**, and that is
 * decided by the caller, not here: the map is handed a list of the people the
 * player perceives this moment and draws exactly those. It never knows where
 * anybody else is, so it cannot give away what perception would not.
 *
 * Drawn to a canvas a few times a second, not every frame. It reads a plain
 * reading and holds no reference to the game.
 */

const SIZE = 172;
/** Metres from the middle to the rim: indoors, and out under the sky. */
const REACH = { inside: 17, outside: 34 };

export function createMinimap({ parent, walls, props, rooms }) {
  const root = document.createElement('div');
  root.className = 'minimap';
  const canvas = document.createElement('canvas');
  const ratio = Math.min(2, window.devicePixelRatio || 1);
  canvas.width = SIZE * ratio;
  canvas.height = SIZE * ratio;
  canvas.style.width = `${SIZE}px`;
  canvas.style.height = `${SIZE}px`;
  const label = document.createElement('div');
  label.className = 'minimap-room';
  root.append(canvas, label);
  parent.append(root);
  const g = canvas.getContext('2d');

  let lastRoom = null;

  /** Draws one reading. */
  function draw(reading) {
    const { player, room, outside, people = [], targets = [] } = reading;
    const reach = outside ? REACH.outside : REACH.inside;
    const scale = (SIZE / 2 - 6) / reach;
    const mid = SIZE / 2;
    const sx = (x) => mid + (x - player.x) * scale;
    const sy = (z) => mid + (z - player.z) * scale;
    const near = (b) => b.max.x > player.x - reach * 1.5 && b.min.x < player.x + reach * 1.5 && b.max.z > player.z - reach * 1.5 && b.min.z < player.z + reach * 1.5;

    g.setTransform(ratio, 0, 0, ratio, 0, 0);
    g.clearRect(0, 0, SIZE, SIZE);
    g.save();
    g.beginPath();
    g.arc(mid, mid, SIZE / 2 - 1, 0, Math.PI * 2);
    g.clip();
    g.fillStyle = 'rgba(12, 15, 22, 0.86)';
    g.fillRect(0, 0, SIZE, SIZE);

    // The room you are in, picked out; the rest of the rooms faintly.
    for (const r of rooms) {
      if (!near(r.bounds)) continue;
      const here = r.id === room;
      if (!here && !r.ceiling) continue;
      g.fillStyle = here ? 'rgba(210, 177, 74, 0.13)' : 'rgba(141, 148, 166, 0.07)';
      g.fillRect(sx(r.bounds.min.x), sy(r.bounds.min.z), (r.bounds.max.x - r.bounds.min.x) * scale, (r.bounds.max.z - r.bounds.min.z) * scale);
    }
    g.fillStyle = 'rgba(141, 148, 166, 0.35)';
    for (const b of props) {
      if (!near(b)) continue;
      g.fillRect(sx(b.min.x), sy(b.min.z), Math.max(1, (b.max.x - b.min.x) * scale), Math.max(1, (b.max.z - b.min.z) * scale));
    }
    g.fillStyle = '#c8ccd4';
    for (const b of walls) {
      if (!near(b)) continue;
      g.fillRect(sx(b.min.x), sy(b.min.z), Math.max(1.5, (b.max.x - b.min.x) * scale), Math.max(1.5, (b.max.z - b.min.z) * scale));
    }

    // The route, the same corners the floor line is drawn along, under
    // everything that moves.
    const route = reading.route;
    if (route && route.length > 1) {
      g.strokeStyle = 'rgba(230, 195, 90, 0.85)';
      g.lineWidth = 3;
      g.lineJoin = 'round';
      g.lineCap = 'round';
      g.setLineDash([5, 4]);
      g.beginPath();
      g.moveTo(sx(route[0].x), sy(route[0].z));
      for (let i = 1; i < route.length; i += 1) g.lineTo(sx(route[i].x), sy(route[i].z));
      g.stroke();
      g.setLineDash([]);
    }

    // People you can see or hear, and nobody else.
    for (const p of people) {
      g.fillStyle = p.talking ? '#f0e6c8' : p.heard && !p.seen ? 'rgba(160, 200, 220, 0.55)' : '#8fc8dc';
      g.beginPath();
      g.arc(sx(p.x), sy(p.z), p.talking ? 3.6 : 3, 0, Math.PI * 2);
      g.fill();
    }

    // Markers, the objective last so it sits on top.
    const ordered = [...targets].sort((a, b) => (a.main ? 1 : 0) - (b.main ? 1 : 0));
    for (const t of ordered) {
      const dx = (t.x - player.x) * scale;
      const dz = (t.z - player.z) * scale;
      const d = Math.hypot(dx, dz);
      const rim = SIZE / 2 - 10;
      const colour = t.kind === 'class' ? '#4f8fe6' : t.kind === 'mission' ? '#f0c040' : t.main ? '#e6c35a' : '#d08a3a';
      if (d > rim) {
        // Off the map: an arrowhead on the rim, pointing at it.
        const a = Math.atan2(dz, dx);
        g.save();
        g.translate(mid + Math.cos(a) * rim, mid + Math.sin(a) * rim);
        g.rotate(a);
        g.fillStyle = colour;
        g.beginPath();
        const s = t.main ? 7 : 5;
        g.moveTo(s, 0);
        g.lineTo(-s * 0.7, s * 0.75);
        g.lineTo(-s * 0.7, -s * 0.75);
        g.closePath();
        g.fill();
        g.restore();
      } else {
        const s = t.main ? 6 : 4;
        g.save();
        g.translate(mid + dx, mid + dz);
        g.rotate(Math.PI / 4);
        g.fillStyle = colour;
        g.strokeStyle = 'rgba(12, 15, 22, 0.9)';
        g.lineWidth = 1.5;
        g.fillRect(-s / 2, -s / 2, s, s);
        g.strokeRect(-s / 2, -s / 2, s, s);
        g.restore();
      }
    }

    // You: an arrow the way you face.
    g.save();
    g.translate(mid, mid);
    g.rotate(Math.atan2(Math.cos(player.yaw), Math.sin(player.yaw)));
    g.fillStyle = '#ffffff';
    g.strokeStyle = 'rgba(12, 15, 22, 0.95)';
    g.lineWidth = 1.5;
    g.beginPath();
    g.moveTo(8, 0);
    g.lineTo(-5, 5.5);
    g.lineTo(-2, 0);
    g.lineTo(-5, -5.5);
    g.closePath();
    g.stroke();
    g.fill();
    g.restore();

    g.restore();
    g.strokeStyle = 'rgba(89, 97, 111, 0.9)';
    g.lineWidth = 2;
    g.beginPath();
    g.arc(mid, mid, SIZE / 2 - 1, 0, Math.PI * 2);
    g.stroke();
    // North.
    g.fillStyle = '#8d94a6';
    g.font = '10px ui-monospace, monospace';
    g.textAlign = 'center';
    g.fillText('N', mid, 12);

    if (reading.roomName !== lastRoom) {
      lastRoom = reading.roomName;
      label.textContent = reading.roomName ?? '';
      label.hidden = !reading.roomName;
    }
  }

  return {
    object: root,
    draw,
  };
}
